import type {
  AvailableAction,
  ContractBooksEffect,
  ReviewDraft,
  ReviewLine,
  Sourced,
} from '@console/types'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE RENDER GATE — `SPEC-LGH-001` §3, acceptance test 1.
 *
 * *"Approve sends only a fingerprint that came from a resolve response **this
 * screen has rendered** — not merely received."*
 *
 * A UI that chained resolve → approve without painting the resolved draft
 * would satisfy every word of `current-state.md` §5.2 while the operator
 * approved something they never saw.
 *
 * ── WHY THIS IS A PURE FUNCTION AND NOT A HOOK ─────────────────────────────
 * The frontend has no component-testing stack (`LGH-007` §3.1), and the
 * estate's most-repeated defect is an unwritten test with a tick beside it.
 * Keeping the decision here means the *rule* is tested today by an ordinary
 * vitest unit test, and only the *wiring* — that the component actually calls
 * it — needs an end-to-end test. Splitting them is what makes one of the two
 * provable now rather than neither.
 * ═══════════════════════════════════════════════════════════════════════════
 */

export interface ApproveGateState {
  /** The draft currently held. */
  draft: ReviewDraft | undefined
  /**
   * The fingerprint the screen has actually painted. Set by the component
   * *after* render, never at fetch time — that difference is the whole gate.
   */
  renderedFingerprint: string | null
  /** A submission in flight; the draft on screen is about to be superseded. */
  submitting: boolean
  /**
   * What the RUN is actually offering — `RunDetail.next_actions`, read the
   * same way `MutateRunPage` already reads it. `undefined` while that fetch
   * has not yet settled.
   *
   * `LGH-027`. A fully-resolved draft (`outstanding: []`) and an approvable
   * run are two different facts: approval binds to `QuoteAssembled.quoteHash`,
   * which the review draft's fingerprint is not, and does not exist just
   * because every question was answered. `PLT-048`'s ruling on
   * `REQ-lighthouse-a-resolved-draft-cannot-be-approved` — resolving the draft
   * is a precondition for assembly, not the same event as it.
   */
  offeredActions: readonly Pick<AvailableAction, 'action' | 'fingerprint'>[] | undefined
}

export type ApproveBlocked =
  | { can: false; because: 'no-draft' }
  | { can: false; because: 'not-rendered' }
  | { can: false; because: 'submitting' }
  | { can: false; because: 'loading-run' }
  | { can: false; because: 'outstanding'; remaining: number }
  | { can: false; because: 'unreadable'; fields: number }
  | { can: false; because: 'not-offered' }
export type ApproveVerdict = { can: true; fingerprint: string } | ApproveBlocked

const isUnreadable = (field: Sourced): boolean => field.kind === 'unreadable'

export function unreadableCount(lines: readonly ReviewLine[]): number {
  return lines.reduce(
    (n, line) => n + [line.item, line.quantity, line.unitPrice].filter(isUnreadable).length,
    0,
  )
}

/**
 * May this screen approve, and if not, why.
 *
 * **`can: true` depends on one thing only: the run's own `next_actions`
 * offering `approve`.** Never on `draft.outstanding` — `LGH-026` found a real
 * draft where `outstanding` reached zero and the run still offered nothing,
 * so a gate keyed on `outstanding` would have enabled a button the platform
 * was always going to refuse. `outstanding` is consulted below only to pick
 * the more specific of two true sentences when approval is not offered; it
 * never flips `can` on its own.
 *
 * **`unreadable` needs no rule of its own** — it fails the render gate on the
 * same grounds everything else does. A screen that could not read a field has
 * not rendered the draft, and `SPEC-LGH-001` §3 already forbids approving one
 * it has not rendered. It is checked separately here only so the operator is
 * told *which* of the two reasons applies.
 */
export function approveGate(state: ApproveGateState): ApproveVerdict {
  const { draft, renderedFingerprint, submitting, offeredActions } = state
  if (draft === undefined) return { can: false, because: 'no-draft' }
  if (submitting) return { can: false, because: 'submitting' }
  if (renderedFingerprint !== draft.fingerprint) return { can: false, because: 'not-rendered' }

  const unreadable = unreadableCount(draft.lines)
  if (unreadable > 0) return { can: false, because: 'unreadable', fields: unreadable }

  if (offeredActions === undefined) return { can: false, because: 'loading-run' }

  const approve = offeredActions.find((a) => a.action === 'approve')
  if (approve !== undefined) return { can: true, fingerprint: approve.fingerprint }

  if (draft.outstanding.length > 0) {
    return { can: false, because: 'outstanding', remaining: draft.outstanding.length }
  }
  return { can: false, because: 'not-offered' }
}

/**
 * What the operator is told, when the button is off.
 *
 * The greyed button is courtesy and the platform's refusal is the guarantee
 * (`CTR-002` §V2.2) — but a disabled control with no reason beside it is the
 * defect the tenant switcher taught this console about: *a control that
 * disappears or dies silently reads as a product decision.*
 */
export function approveBlockedSays(blocked: ApproveBlocked): string {
  switch (blocked.because) {
    case 'no-draft':
      return 'Nothing to approve yet.'
    case 'submitting':
      return 'Saving your decisions…'
    case 'loading-run':
      return 'Checking what this run can do…'
    case 'not-rendered':
      return 'This draft changed. Reload it before approving, so what you approve is what you saw.'
    case 'unreadable':
      return blocked.fields === 1
        ? 'One field on this quote cannot be read by this console, so it cannot be approved here.'
        : `${String(blocked.fields)} fields on this quote cannot be read by this console, so it cannot be approved here.`
    case 'outstanding':
      return blocked.remaining === 1
        ? 'One question still needs your answer.'
        : `${String(blocked.remaining)} questions still need your answer.`
    case 'not-offered':
      /**
       * `LGH-027`. Every question answered and the run still offers no
       * approval — the exact dead end `LGH-026` found and `MutateRunPage`
       * already refuses on its own side (`"This run isn't waiting on a
       * decision…"`). `PLT-048` owes the words for *why*; until they land,
       * this states the honest fact and stops rather than guessing at what
       * comes next.
       */
      return "This run isn't offering an approval right now."
  }
}

/**
 * The sentence for `books_effect`, rendered at the one place it matters —
 * the approve control, at the moment of commitment — never as a standing
 * badge. `LGH-018`/`LGH-026`'s own argument for asking Cadence for the field.
 */
export function booksEffectSays(effect: ContractBooksEffect): string {
  return effect === 'writes'
    ? 'Approving this will write to your books.'
    : 'This is read-only. Approving it will not write anything to your books.'
}
